/** /app/activity — the complete activity history, newest first, by category, grouped by day. */
import { useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import {
  ACTIVITY_FILTERS,
  ActivityItem,
  activityHref,
  useMarkActivity,
  type ActivityCategory,
  type ActivityEvent,
  type ActivityPage as Page,
} from "./activity.js";

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(Date.now() - 86_400_000);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export function ActivityPage(): ReactNode {
  const [filter, setFilter] = useState<ActivityCategory | "all">("all");
  const navigate = useNavigate();
  const mark = useMarkActivity();

  const feed = useInfiniteQuery({
    queryKey: ["activity", "page", filter],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limit: "40" });
      if (pageParam) p.set("before", pageParam);
      if (filter !== "all") p.set("category", filter);
      return api.get<Page>(`/activity?${p.toString()}`);
    },
    getNextPageParam: (last) => last.nextBefore,
  });

  const events = feed.data?.pages.flatMap((p) => p.events) ?? [];
  const unread = feed.data?.pages[0]?.unread ?? 0;
  const groups: { day: string; items: ActivityEvent[] }[] = [];
  for (const e of events) {
    const day = dayLabel(e.at);
    const g = groups[groups.length - 1];
    if (g && g.day === day) g.items.push(e);
    else groups.push({ day, items: [e] });
  }

  const openEvent = (e: ActivityEvent): void => {
    if (!e.read) mark.mutate({ keys: [e.key], read: true });
    const href = activityHref(e);
    if (href) navigate(href);
  };

  return (
    <section className="t1-act-page">
      <div className="page-h">
        <div>
          <h1>Activity</h1>
          <div className="sub">Everything that happened across your requirements and candidates — newest first.</div>
        </div>
        {unread > 0 && (
          <button type="button" className="btn" disabled={mark.isPending} onClick={() => mark.mutate({ all: true })}>
            Mark all as read
          </button>
        )}
      </div>

      <div className="card">
        <div className="t1-act-filters">
          <div className="seg" role="tablist" aria-label="Filter activity">
            {ACTIVITY_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={filter === f.key}
                className={filter === f.key ? "on" : ""}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
          {unread > 0 && <span className="mut sm">{unread > 99 ? "99+" : unread} unread</span>}
        </div>

        <div className="card-b">
          {feed.isLoading && <p className="mut sm">Loading activity…</p>}
          {feed.isError && <p className="mut sm">Couldn't load activity. Please retry.</p>}
          {feed.isSuccess && events.length === 0 && (
            <p className="mut sm">{filter === "all" ? "Nothing has happened yet." : "No activity of this kind yet."}</p>
          )}
          {groups.map((g) => (
            <div key={g.day} className="t1-act-group">
              <div className="t1-act-day">{g.day}</div>
              {g.items.map((e) => (
                <ActivityItem
                  key={e.key}
                  e={e}
                  onOpen={openEvent}
                  trailing={
                    <button
                      type="button"
                      className="btn sm ghost t1-act-toggle"
                      disabled={mark.isPending}
                      onClick={() => mark.mutate({ keys: [e.key], read: !e.read })}
                    >
                      {e.read ? "Mark unread" : "Mark read"}
                    </button>
                  }
                />
              ))}
            </div>
          ))}
          {feed.hasNextPage && (
            <button type="button" className="btn sm" disabled={feed.isFetchingNextPage} onClick={() => void feed.fetchNextPage()}>
              {feed.isFetchingNextPage ? "Loading…" : "Load older activity"}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
